/**
 * useWallet.js
 *
 * Supports any number of installed EVM wallets (MetaMask, Rabby, OKX,
 * Coinbase Wallet, Phantom, Trust Wallet, and anything else that speaks
 * EIP-1193/EIP-6963) via walletDiscovery.js, rather than assuming a
 * single `window.ethereum`.
 *
 * Security notes:
 *   - Event listeners (accountsChanged/chainChanged/disconnect) are
 *     attached to the SPECIFIC provider object the user picked, and torn
 *     down before attaching to a different one — never left dangling on
 *     a stale/abandoned provider.
 *   - `chainId` in React state is a UI convenience only. Anything that
 *     actually sends a transaction re-reads the chain ID fresh from the
 *     provider immediately beforehand (see useMintContract's runTx) —
 *     state can go stale if the user flips networks in their wallet
 *     between renders, a live check right before signing can't.
 *   - `disconnect()` only clears local UI state — injected wallets don't
 *     expose a real programmatic disconnect, so the extension itself is
 *     still connected until the user revokes it there. We say so in the UI.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { discoverWallets } from '../lib/walletDiscovery';
import { ARC_NETWORK, ensureArcNetwork } from '../lib/chain';

export default function useWallet() {
  const [availableWallets, setAvailableWallets] = useState([]);
  const [walletName, setWalletName] = useState(null);
  const [address, setAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const providerRef = useRef(null);      // the raw EIP-1193 provider of the connected wallet
  const listenersRef = useRef(null);     // { onAccountsChanged, onChainChanged } currently attached

  const isCorrectNetwork = chainId === ARC_NETWORK.chainIdDec;

  // ── Discover installed wallets on mount, keep listening for late announcers ──
  useEffect(() => {
    const stop = discoverWallets(setAvailableWallets);
    return stop;
  }, []);

  const detachListeners = useCallback(() => {
    const provider = providerRef.current;
    const listeners = listenersRef.current;
    if (provider && listeners) {
      provider.removeListener?.('accountsChanged', listeners.onAccountsChanged);
      provider.removeListener?.('chainChanged', listeners.onChainChanged);
      provider.removeListener?.('disconnect', listeners.onDisconnect);
    }
    listenersRef.current = null;
  }, []);

  const attachListeners = useCallback((provider) => {
    const onAccountsChanged = (accounts) => setAddress(accounts && accounts.length ? accounts[0] : null);
    const onChainChanged = (hexChainId) => setChainId(parseInt(hexChainId, 16));
    const onDisconnect = () => {
      setAddress(null);
      setChainId(null);
    };
    provider.on?.('accountsChanged', onAccountsChanged);
    provider.on?.('chainChanged', onChainChanged);
    provider.on?.('disconnect', onDisconnect);
    listenersRef.current = { onAccountsChanged, onChainChanged, onDisconnect };
  }, []);

  const connect = useCallback(async (walletUid) => {
    setError(null);
    const wallet = availableWallets.find((w) => w.uid === walletUid);
    if (!wallet) {
      setError('That wallet is no longer available — try rescanning.');
      return;
    }
    setConnecting(true);
    try {
      const provider = wallet.provider;

      // ── Force a real approval prompt, every time ──────────────────
      // `eth_requestAccounts` reconnects SILENTLY once a site has been
      // approved before — no dialog, no chance to pick an account. That
      // is what made this feel like it was connecting on its own.
      //
      // Revoking the existing permission first guarantees the wallet has
      // nothing cached to auto-approve against, so the next request must
      // surface its dialog. Both methods are EIP-2255; wallets that
      // don't implement them throw and we fall through.
      try {
        await provider.request({
          method: 'wallet_revokePermissions',
          params: [{ eth_accounts: {} }],
        });
      } catch { /* not supported — requestPermissions below still prompts */ }

      let granted = false;
      try {
        const perms = await provider.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }],
        });
        granted = Array.isArray(perms) && perms.length > 0;
      } catch (permErr) {
        // 4001 = user clicked reject. Honour it; do not fall through to
        // eth_requestAccounts, which would connect them anyway.
        if (permErr?.code === 4001) {
          throw Object.assign(new Error('Connection request rejected in wallet.'), { code: 4001 });
        }
        /* method unsupported — eth_requestAccounts will prompt instead */
      }

      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      if (!accounts || !accounts.length) {
        throw new Error('Wallet did not return an account. Approve the connection and try again.');
      }
      void granted;

      // Connected — now make sure they are on the right network. Ask
      // straight away rather than waiting until a mint is attempted, so
      // a transaction is never prepared against the wrong chain.
      let hexChainId = await provider.request({ method: 'eth_chainId' });
      if (parseInt(hexChainId, 16) !== ARC_NETWORK.chainIdDec) {
        try {
          await ensureArcNetwork(provider);
          hexChainId = await provider.request({ method: 'eth_chainId' });
        } catch (netErr) {
          // They declined the switch. Stay connected but flagged as wrong
          // network — every send path re-checks and will refuse.
          setError(
            netErr?.code === 4001
              ? `Network switch declined. Switch to ${ARC_NETWORK.chainName} before minting.`
              : (netErr?.message || 'Could not switch network.')
          );
        }
      }

      // Swap listeners over to the newly-selected provider
      detachListeners();
      attachListeners(provider);
      providerRef.current = provider;

      setWalletName(wallet.name);
      setAddress(accounts[0]);
      setChainId(parseInt(hexChainId, 16));
    } catch (err) {
      if (err?.code === 4001) {
        setError('Connection request rejected in wallet.');
      } else {
        setError(err?.message || 'Failed to connect wallet.');
      }
    } finally {
      setConnecting(false);
    }
  }, [availableWallets, detachListeners, attachListeners]);

  const disconnect = useCallback(() => {
    detachListeners();
    providerRef.current = null;
    setWalletName(null);
    setAddress(null);
    setChainId(null);
  }, [detachListeners]);

  const switchToArc = useCallback(async () => {
    setError(null);
    if (!providerRef.current) return;
    try {
      await ensureArcNetwork(providerRef.current);
      const hexChainId = await providerRef.current.request({ method: 'eth_chainId' });
      setChainId(parseInt(hexChainId, 16));
    } catch (err) {
      setError(err?.message || 'Failed to switch network.');
    }
  }, []);

  /**
   * Returns a signer bound to the connected wallet's own provider —
   * and re-verifies, at the moment of the call, that it's still pointed
   * at ARC Testnet, rather than trusting the `chainId` in React state.
   */
  const getSigner = useCallback(async () => {
    const provider = providerRef.current;
    if (!provider) throw new Error('No wallet connected.');
    const hexChainId = await provider.request({ method: 'eth_chainId' });
    const liveChainId = parseInt(hexChainId, 16);
    if (liveChainId !== ARC_NETWORK.chainIdDec) {
      setChainId(liveChainId); // resync UI state with reality
      throw new Error(`Wrong network in wallet — switch to ${ARC_NETWORK.chainName} and try again.`);
    }
    const browserProvider = new ethers.BrowserProvider(provider);
    return browserProvider.getSigner();
  }, []);

  useEffect(() => () => detachListeners(), [detachListeners]);

  return {
    availableWallets,
    walletName,
    address,
    chainId,
    connecting,
    error,
    isCorrectNetwork,
    isConnected: Boolean(address),
    connect,
    disconnect,
    switchToArc,
    getSigner,
  };
}
